import {view, css} from '@sail/ui/deprecated';
import {assignTokens, Icon, tokens} from '@sail/ui';
import {thumbsUp, thumbsDown} from '@sail/icons/react/Icon';
import {defineMessages, useIntl} from 'react-intl';

import {useAnalytics} from '@stripe-internal/inline-data';
import {
  dotComColors,
  dotComTransitions,
  useTheme,
} from '@stripe-internal/conversations-ui';
import {useState} from 'react';
import {answerFeedback} from './analytics';

const messages = defineMessages({
  thumbsUpAriaLabel: {
    id: 'mse_inline_data.feedback.thumbs_up.ariaLabel',
    description: 'Label for the LLM feedback thumbs up',
    defaultMessage: 'Good response',
  },
  thumbsDownAriaLabel: {
    id: 'mse_inline_data.feedback.thumbs_down.ariaLabel',
    description: 'Label for the LLM feedback thumbs down',
    defaultMessage: 'Bad response',
  },
});

type FeedbackButtonProps = {
  icon: typeof thumbsUp | typeof thumbsDown;
  ariaLabel: string;
  isSelected: boolean;
  onClick: () => void;
};

const FeedbackButton = ({
  icon,
  ariaLabel,
  isSelected,
  onClick,
}: FeedbackButtonProps) => {
  const theme = useTheme();

  return (
    <view.button
      aria-label={ariaLabel}
      css={{
        height: css.value('24px'),
        width: css.value('24px'),
        stack: 'x',
        alignX: 'center',
        alignY: 'center',
        border: css.value('1px solid'),
        borderColor: isSelected
          ? css.value(theme.linkColor)
          : css.value(dotComColors.brand300),
        background: isSelected
          ? css.value(theme.linkColor)
          : css.value(dotComColors.brand),
        borderRadius: 'medium',
        transition: css.value(dotComTransitions.buttonTransition),
        '&:focus-visible': {
          outline: theme.focusVisibleOutline,
        },
        color: isSelected
          ? css.value(dotComColors.brand)
          : css.value(dotComColors.brand600),
        '&:hover': isSelected
          ? {}
          : {
              color: css.value(theme.linkColor),
              borderColor: css.value('#c4ccd8'),
              boxShadow: css.value(
                '0 0.25rem 0.75rem 0 rgba(0, 0, 0, 0.06), 0 0.125rem 0.25rem 0 rgba(0, 0, 0, 0.1)',
              ),
            },
      }}
      onClick={isSelected ? undefined : onClick}
    >
      <Icon icon={icon} size="xsmall" css={{fill: 'currentColor'}} />
    </view.button>
  );
};

export type FeedbackProps = {
  streamingConversationId?: string;
};

export const Feedback = (props: FeedbackProps) => {
  const {streamingConversationId} = props;

  const [selectedFeedback, setSelectedFeedback] = useState<
    'thumbs_up' | 'thumbs_down' | undefined
  >();
  const analytics = useAnalytics();
  const intl = useIntl();

  return (
    <view.div
      uses={[
        assignTokens(tokens.color.icon.action.primary, dotComColors.brand600),
      ]}
      css={{gap: 'xsmall', stack: 'x'}}
    >
      <FeedbackButton
        icon={thumbsUp}
        ariaLabel={intl.formatMessage(messages.thumbsUpAriaLabel)}
        isSelected={selectedFeedback === 'thumbs_up'}
        onClick={() => {
          setSelectedFeedback('thumbs_up');
          analytics.action(answerFeedback, {
            feedback: 'thumbs_up',
            streaming_conversation_id: streamingConversationId,
          });
        }}
      />
      <FeedbackButton
        icon={thumbsDown}
        ariaLabel={intl.formatMessage(messages.thumbsDownAriaLabel)}
        isSelected={selectedFeedback === 'thumbs_down'}
        onClick={() => {
          setSelectedFeedback('thumbs_down');
          analytics.action(answerFeedback, {
            feedback: 'thumbs_down',
            streaming_conversation_id: streamingConversationId,
          });
        }}
      />
    </view.div>
  );
};
